import type { Part } from "../catalog/schema";

export type BasketItem = {
  partId: string;       // = artNr
  meshName: string;
  name: string;         // localized name snapshot
  color: string;
  qty: number;
  addedAt: number;
};

type Listener = (items: BasketItem[]) => void;

export class Basket {
  private items: BasketItem[] = [];
  private listeners = new Set<Listener>();
  private storageKey: string;

  constructor(bedId: string) {
    this.storageKey = `kb-spf:basket:${bedId}`;
    this.load();
  }

  private load() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw) this.items = JSON.parse(raw) as BasketItem[];
    } catch {
      this.items = [];
    }
  }

  private persist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
    } catch {
      /* localStorage full / disabled — silently degrade */
    }
  }

  list(): readonly BasketItem[] {
    return this.items;
  }

  count(): number {
    return this.items.reduce((sum, i) => sum + i.qty, 0);
  }

  rows(): number {
    return this.items.length;
  }

  add(part: Part, qty: number) {
    const existing = this.items.find((i) => i.partId === part.artNr);
    if (existing) {
      existing.qty = Math.min(99, existing.qty + qty);
    } else {
      this.items.push({
        partId: part.artNr,
        meshName: part.meshName,
        name: part.name.de,
        color: part.color ?? "#cccccc",
        qty: Math.max(1, Math.min(99, qty)),
        addedAt: Date.now(),
      });
    }
    this.persist();
    this.notify();
  }

  setQty(partId: string, qty: number) {
    const item = this.items.find((i) => i.partId === partId);
    if (!item) return;
    if (qty <= 0) {
      this.remove(partId);
      return;
    }
    item.qty = Math.min(99, qty);
    this.persist();
    this.notify();
  }

  remove(partId: string) {
    this.items = this.items.filter((i) => i.partId !== partId);
    this.persist();
    this.notify();
  }

  clear() {
    this.items = [];
    this.persist();
    this.notify();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.items);
    return () => this.listeners.delete(fn);
  }

  private notify() {
    for (const l of this.listeners) l(this.items);
  }
}
